import { CSSProperties } from 'react';
import {
	formatFontFamily,
	formatLineHeight,
	formatUnit,
} from '../../stickyBanner.helper';
import { MediaSettings, TextAlign, TypographySettings } from '../../types';

/**
 * Inline style for a text element. The font size goes through the
 * `--sb-font-size` variable so small screens can cap it in CSS.
 *
 * @param settings Typography settings.
 */
export const getTextStyle = ( settings: TypographySettings ): CSSProperties =>
	( {
		fontFamily: formatFontFamily( settings.fontFamily ),
		fontWeight: settings.weight || undefined,
		'--sb-font-size': formatUnit( settings.size ),
		lineHeight: formatLineHeight( settings.lineHeight ),
		color: settings.color || undefined,
		textAlign: settings.align,
	} ) as CSSProperties;

/**
 * @param settings Media settings.
 */
export const getImageStyle = ( settings: MediaSettings ): CSSProperties => ( {
	width: formatUnit( settings.width ),
	height: formatUnit( settings.height ),
	borderRadius: formatUnit( settings.borderRadius ),
} );

/**
 * A numeric style value as px ("" when not a number).
 *
 * @param value Style value, e.g. "32".
 */
export const px = ( value: string | undefined ): string | undefined =>
	formatUnit( value );

/** Sides of a padding style field, in CSS order. */
export const PADDING_SIDES = [ 'Top', 'Right', 'Bottom', 'Left' ] as const;

/**
 * Padding or margin set in a component's four-side field (see StyleField).
 * Sides left empty keep the style's own value.
 *
 * @param style    Component style values.
 * @param key      Field key.
 * @param property CSS property.
 */
const getSidesStyle = (
	style: Record< string, string >,
	key: string,
	property: 'padding' | 'margin'
): CSSProperties =>
	PADDING_SIDES.reduce( ( all, side ) => {
		const value = px( style[ `${ key }${ side }` ] );
		return value ? { ...all, [ `${ property }${ side }` ]: value } : all;
	}, {} as CSSProperties );

/**
 * @param style Component style values.
 * @param key   Padding field key.
 */
export const getPaddingStyle = (
	style: Record< string, string >,
	key = 'padding'
): CSSProperties => getSidesStyle( style, key, 'padding' );

/**
 * @param style Component style values.
 * @param key   Margin field key.
 */
export const getMarginStyle = (
	style: Record< string, string >,
	key = 'margin'
): CSSProperties => getSidesStyle( style, key, 'margin' );

/** Hover style values every component can have (empty = no change). */
const HOVER_KEYS = {
	hoverText: { variable: '--sb-hover-color', className: 'has-hover-color' },
	hoverBg: { variable: '--sb-hover-bg', className: 'has-hover-bg' },
	hoverBorder: {
		variable: '--sb-hover-border',
		className: 'has-hover-border',
	},
	hoverLabel: { variable: '--sb-hover-label', className: 'has-hover-label' },
	hoverArea: { variable: '--sb-hover-area', className: 'has-hover-area' },
} as const;

export type HoverKey = keyof typeof HOVER_KEYS;

/**
 * Hover colors as CSS variables plus the classes that switch the hover
 * rules on (components' CSS reads them on :hover).
 *
 * @param style Component style values.
 * @param keys  Hover values this component (variant) supports.
 */
export const getHoverProps = (
	style: Record< string, string >,
	keys: HoverKey[]
): { style: CSSProperties; className: string } =>
	keys.reduce(
		( all, key ) => {
			const value = style[ key ];
			if ( ! value || ! /^#[0-9a-f]{3,8}$/i.test( value ) ) {
				return all;
			}
			return {
				style: { ...all.style, [ HOVER_KEYS[ key ].variable ]: value },
				className: `${ all.className } ${ HOVER_KEYS[ key ].className }`,
			};
		},
		{ style: {} as CSSProperties, className: '' }
	);

/**
 * Class for a text link's underline option.
 *
 * @param value "none", "hover" (default) or "always".
 */
export const getUnderlineClass = ( value: string | undefined ): string =>
	` is-underline-${
		value === 'none' || value === 'always' ? value : 'hover'
	}`;

/**
 * Text alignment as a flex position (justify-content / align-self).
 *
 * @param align Text alignment.
 */
export const alignToFlex = (
	align: TextAlign | undefined
): 'flex-start' | 'center' | 'flex-end' | undefined => {
	if ( align === 'left' ) {
		return 'flex-start';
	}
	if ( align === 'right' ) {
		return 'flex-end';
	}
	return align === 'center' ? 'center' : undefined;
};
